import { Ion } from "cesium";
import {
  isRouteErrorResponse,
  Links,
  Meta,
  Outlet,
  Scripts,
  ScrollRestoration,
  useLoaderData,
} from "react-router";
import { Provider } from "react-redux";
import store from "./store";
import useInitializeUser from "./customHooks/useInitializeUser";
// import useNotifications from "./customHooks/useNotifications";
import type { Route } from "./+types/root";
import "./app.css";
import library from './assets/icons/'
import { ThemeProvider } from "./utils/theme";
import Modal from "./containers/Modal/Modal";
import { ModalProvider } from "./components/ModalContext";
import Toast from "./components/Toast";
import 'react-pdf/dist/Page/TextLayer.css';
import 'react-pdf/dist/Page/AnnotationLayer.css';

// Runs on the server only — process.env is populated from the k8s secret here.
export function loader() {
  return { ionAccessToken: process.env.REACT_APP_CESIUM_ACCESS_TOKEN || "" };
}

export const initialLoadTime = performance.now();
console.log("Initial load time recorded:", initialLoadTime);
export const links: Route.LinksFunction = () => [
  { rel: "preconnect", href: "https://fonts.googleapis.com" },
  {
    rel: "preconnect",
    href: "https://fonts.gstatic.com",
    crossOrigin: "anonymous",
  },
  {
    rel: "stylesheet",
    href: "https://fonts.googleapis.com/css2?family=Inter:ital,opsz,wght@0,14..32,100..900;1,14..32,100..900&display=swap",
  },
];

export function Layout({ children }: { children: React.ReactNode }) {
  return (
    <html lang="en">
      <head>
        <meta charSet="utf-8" />
        <meta name="viewport" content="width=device-width, initial-scale=1" />
        <Meta />
        <Links />
      </head>
      <body>
        {children}
        <ScrollRestoration />
        <Scripts />
      </body>
    </html>
  );
}

const AppContent = () => {
  useInitializeUser();
  // useNotifications();
  
  return (
    <ThemeProvider>
      <ModalProvider>
        <Outlet />
        <Toast />
      </ModalProvider>
    </ThemeProvider>
  );
};

const App = () => {
  const { ionAccessToken } = useLoaderData<typeof loader>();

  Ion.defaultAccessToken = ionAccessToken;

  return (
    <Provider store={store}>
      <AppContent />
    </Provider>
  );
};

export default App;

export function ErrorBoundary({ error }: Route.ErrorBoundaryProps) {
  let message = "Oops!";
  let details = "An unexpected error occurred.";
  let stack: string | undefined;

  if (isRouteErrorResponse(error)) {
    message = error.status === 404 ? "404" : "Error";
    details =
      error.status === 404
        ? "The requested page could not be found."
        : error.statusText || details;
  } else if (import.meta.env.DEV && error && error instanceof Error) {
    details = error.message;
    stack = error.stack;
  }

  return (
    <main className="pt-16 p-4 container mx-auto">
      <h1>{message}</h1>
      <p>{details}</p>
      {stack && (
        <pre className="w-full p-4 overflow-x-auto">
          <code>{stack}</code>
        </pre>
      )}
    </main>
  );
}
